import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Input } from '@/components/ui/input';

/**
 * Personel listesinden ad-soyad seçtiren otomatik tamamlama alanı.
 * Listede olmayan bir isim de serbestçe yazılabilir (misafir/dış personel).
 *
 * @param {Array}  personnel  [{ id, full_name, unvan, department }]
 * @param {string} value      seçili ad soyad
 * @param {func}   onChange   (fullName) => void
 * @param {func}   onSelect   (personnelRecord) => void — listeden seçildiğinde
 */
const PersonnelNameInput = ({ personnel = [], value = '', onChange, onSelect, placeholder }) => {
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const containerRef = useRef(null);

  // Dışarı tıklanınca listeyi kapat
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const matches = useMemo(() => {
    const term = String(value || '').toLocaleLowerCase('tr-TR').trim();
    const list = term
      ? personnel.filter(p =>
          p.full_name.toLocaleLowerCase('tr-TR').includes(term) ||
          (p.unvan || '').toLocaleLowerCase('tr-TR').includes(term)
        )
      : personnel;
    return list.slice(0, 30);
  }, [personnel, value]);

  const choose = (p) => {
    onChange(p.full_name);
    if (onSelect) onSelect(p);
    setOpen(false);
  };

  const handleKeyDown = (e) => {
    if (!open || matches.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight(h => Math.min(h + 1, matches.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight(h => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      choose(matches[highlight]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <div className="relative" ref={containerRef}>
      <Input
        value={value || ''}
        onChange={(e) => {
          onChange(e.target.value);
          setHighlight(0);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder || 'Personel ara veya yaz'}
        className="h-8 text-sm"
        autoComplete="off"
      />

      {open && matches.length > 0 && (
        <ul className="absolute z-50 mt-1 max-h-56 w-full overflow-auto rounded-md border bg-white shadow-lg">
          {matches.map((p, i) => (
            <li
              key={p.id}
              onMouseDown={(e) => {
                e.preventDefault();
                choose(p);
              }}
              onMouseEnter={() => setHighlight(i)}
              className={`cursor-pointer px-3 py-1.5 text-sm ${
                i === highlight ? 'bg-primary/10' : ''
              }`}
            >
              <span className="font-medium">{p.full_name}</span>
              {p.unvan && <span className="text-muted-foreground"> — {p.unvan}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default PersonnelNameInput;
